/**
 * SMALL MULTIPLES — the same chart, once per building, on one shared scale.
 *
 * ── THE QUESTION IT ANSWERS ──
 *
 * "Which of my buildings changed shape this month?" A ranked list gives totals
 * and hides the shape; the outlier scatter gives one number per building and
 * hides time. Ten identical little charts side by side, on ONE y-scale, answers
 * it in a glance — a building that fell off a cliff on the 12th is visible
 * without reading a single figure.
 *
 * ── WHY IT COSTS ONE REQUEST PER BUILDING, AND WHY THAT IS SAID OUT LOUD ──
 *
 * `POST /analytics/batch` takes up to twelve panels but ONE scope: `buildingId`
 * is read from the request body and applied to every panel in it (hub.js), and
 * `/analytics/breakdown` returns exactly one dimension per call. There is no
 * `building x day` request in this API. So the only way to get a per-building
 * time series is one scoped request per building, and against a production
 * connection pool of FIVE that is not something to do quietly on page load.
 *
 * Everything below follows from that:
 *
 *   · IT IS OFF BY DEFAULT and the control says what it will cost before the
 *     reader presses it. A panel that silently fires eight requests is a panel
 *     that makes the whole screen feel broken on a slow connection.
 *   · IT IS LOCAL STATE, not part of the filter. A saved view that fired eight
 *     extra requests every time somebody opened it would be a trap set months
 *     earlier by somebody who has forgotten.
 *   · IT IS CAPPED at the busiest few buildings, and it SAYS how many it left
 *     out. A silent top-N is the same lie as a truncated table.
 *   · EVERY REQUEST GOES THROUGH THE SAME SEMAPHORE as the rest of the screen,
 *     at the LOWEST priority, marked as an analytics call. So at most two are in
 *     flight at once, they queue behind every panel the reader is actually
 *     looking at, and changing a filter drops the ones that have not started.
 *
 * ── ONE SCALE, OR IT IS NOT A SMALL MULTIPLE ──
 *
 * Every tile shares a y-domain computed across all of them. Per-tile
 * auto-scaling is the classic way to draw this chart wrongly: a building with
 * four entries and one with four hundred then look identical, which is the exact
 * comparison the panel exists to make.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AnalyticsBatchResult,
  AnalyticsBreakdown,
  fetchAnalyticsBreakdown,
  isBatchFailure,
} from '../../../../../api/analytics-client';
import { dataViz } from '../../../../../style-utils/theme';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { DashSimpleTooltip } from '../charts/chart-tooltip';
import { ChartArea } from '../charts/chart.styled';
import {
  GRADIENT,
  cursor,
  fillUrl,
  format,
  margins,
  soloPoint,
  xAxisTime,
  yAxisCount,
} from '../charts/chart-theme';
import { budget, isSuperseded } from '../data/request-budget';
import { scopeQuery } from '../data/dashboard-data';
import { timeDimension } from '../data/panel-plan';
import { DashboardFilter, FilterMeta } from '../state/filter-model';
import {
  MeterMeta,
  MultiplesGrid,
  MultiplesTile,
  MultiplesTitle,
  PanelSection,
  PanelToggleRow,
  SectionCaption,
  StatePill,
} from './panels.styled';
import { PanelToggle } from '../charts/chart.styled';

const PANEL_ID = 'multiples';

/** The cap. Six tiles is the most that stays readable at a desk width, and six
 *  requests is the most this screen will spend on a panel the reader has to ask
 *  for. Both numbers are the same number by coincidence, not by derivation. */
export const MULTIPLES_CAP = 6;

interface Tile {
  id: number;
  name: string;
  points: { key: string; label: string; value: number }[];
  total: number;
  error?: string;
}

/** The buildings worth drawing, busiest first, from the league panel the screen
 *  already has. Reusing that result rather than asking again is what keeps the
 *  cost at N and not N+1. */
export const tileBuildings = (
  league: AnalyticsBatchResult | undefined,
  meta: FilterMeta | undefined,
  cap = MULTIPLES_CAP
): { id: number; name: string }[] => {
  if (!league || isBatchFailure(league)) return [];

  const named = league.items
    .map(item => ({
      id: Number(item.key),
      name: item.label,
      value: format.numberOf(item.value) ?? 0,
    }))
    .filter(row => Number.isInteger(row.id) && row.id > 0);

  // A building that recorded nothing has a flat line, which IS a finding — but
  // not one worth a request when the ranked list already says it in one row.
  const withActivity = named.filter(row => row.value > 0);
  const source = withActivity.length ? withActivity : named;

  return source
    .sort((a, b) => b.value - a.value)
    .slice(0, cap)
    .map(row => ({
      id: row.id,
      name: meta?.buildings.find(b => b.id === row.id)?.name || row.name,
    }));
};

export interface SmallMultiplesPanelProps {
  filter: DashboardFilter;
  meta?: FilterMeta;
  league?: AnalyticsBatchResult;
  state: ChartShellState;
  onSelectBuilding: (id: number, name: string) => void;
}

export const SmallMultiplesPanel: React.FC<SmallMultiplesPanelProps> = ({
  filter,
  meta,
  league,
  state,
  onSelectBuilding,
}) => {
  const [on, setOn] = useState(false);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [loading, setLoading] = useState(false);

  const buildings = useMemo(
    () => tileBuildings(league, meta),
    [league, meta]
  );

  const totalBuildings = useMemo(
    () =>
      league && !isBatchFailure(league)
        ? league.items.filter(i => Number(i.key) > 0).length
        : 0,
    [league]
  );

  /* The key every request is answering. A filter change makes a new key, the old
     answers are dropped on arrival, and anything still queued is never sent. */
  const key = useMemo(
    () =>
      JSON.stringify([
        scopeQuery(filter),
        filter.measure,
        filter.granularity,
        buildings.map(b => b.id),
      ]),
    [filter, buildings]
  );

  const wanted = useRef('');
  const controllers = useRef<AbortController[]>([]);

  const load = useCallback(() => {
    if (!on || !buildings.length) return;
    wanted.current = key;
    setLoading(true);
    setTiles([]);

    const dimension = timeDimension(filter.granularity);
    const base = scopeQuery(filter);

    controllers.current.forEach(c => c.abort());
    controllers.current = [];

    const jobs = buildings.map(building => {
      const controller = new AbortController();
      controllers.current.push(controller);

      return budget
        .submit<AnalyticsBreakdown>({
          // LOWEST PRIORITY ON THE SCREEN. This panel is context; every other
          // request is the answer.
          priority: 9,
          analytics: true,
          stillWanted: () => wanted.current === key,
          run: () =>
            fetchAnalyticsBreakdown(
              {
                ...base,
                buildingId: building.id,
                measure: filter.measure,
                dimension,
              },
              { signal: controller.signal }
            ),
        })
        .then<Tile>(data => ({
          id: building.id,
          name: building.name,
          points: (data.items || []).map(item => ({
            key: item.key,
            label: format.dayKey(item.key),
            value: format.numberOf(item.value) ?? 0,
          })),
          total: format.numberOf(data.total) ?? 0,
        }))
        .catch((error: unknown): Tile => {
          if (isSuperseded(error)) {
            return { id: building.id, name: building.name, points: [], total: 0 };
          }

          // ONE TILE'S FAILURE IS ONE TILE. The others are still true.
          return {
            id: building.id,
            name: building.name,
            points: [],
            total: 0,
            error:
              (error as { message?: string })?.message || 'This building did not answer.',
          };
        });
    });

    Promise.all(jobs).then(results => {
      if (wanted.current !== key) return;
      setTiles(results);
      setLoading(false);
    });
  }, [on, buildings, key, filter]);

  useEffect(() => {
    load();

    return () => {
      controllers.current.forEach(c => c.abort());
    };
  }, [load]);

  /* ONE DOMAIN ACROSS EVERY TILE. See the header: per-tile auto-scaling makes a
     building with four entries look like one with four hundred. */
  const max = tiles.reduce(
    (top, tile) => tile.points.reduce((t, p) => Math.max(t, p.value), top),
    0
  );
  const domain: [number, number] = [0, max > 0 ? max : 1];

  const hidden = Math.max(0, totalBuildings - buildings.length);
  const bucket = filter.granularity === 'week' ? 'week' : 'day';

  return (
    <PanelShell
      id={PANEL_ID}
      title="Every building, side by side"
      wide
      state={state}
      caveat={`The same chart once per building, on ONE shared scale, so a site that changed shape is visible without reading a figure. This is the only panel on this screen that costs a request PER BUILDING — the API has no building-by-${bucket} question — so it is off until you ask for it.`}
      emptyReason={
        !buildings.length && !state.loading
          ? {
              title: 'No buildings to lay side by side',
              text: 'Nothing in scope recorded anything in this period, so there are no series to compare.',
            }
          : null
      }
    >
      <PanelSection>
        <PanelToggleRow>
          <PanelToggle
            type="button"
            active={on}
            aria-pressed={on}
            data-testid="multiples-toggle"
            onClick={() => setOn(value => !value)}
          >
            {on ? 'Hide the per-building charts' : 'Draw one chart per building'}
          </PanelToggle>
          {/* THE COST, BEFORE THE CLICK. */}
          {!on && buildings.length ? (
            <SectionCaption data-testid="multiples-cost">
              {buildings.length} more {buildings.length === 1 ? 'request' : 'requests'},
              one per building, queued behind everything else on this screen.
            </SectionCaption>
          ) : null}
        </PanelToggleRow>

        {on ? (
          <>
            <MeterMeta data-testid="multiples-meta">
              <span>
                {buildings.length} of {totalBuildings || buildings.length}{' '}
                {totalBuildings === 1 ? 'building' : 'buildings'}, busiest first
              </span>
              {/* NEVER A SILENT TOP-N. */}
              {hidden ? (
                <StatePill tone="neutral" data-testid="multiples-capped">
                  {hidden} more {hidden === 1 ? 'building is' : 'buildings are'} not
                  drawn — the ranked list above has every one of them
                </StatePill>
              ) : null}
              {max > 0 ? (
                <span data-testid="multiples-scale">
                  All tiles share one scale, 0 to {max.toLocaleString()}
                </span>
              ) : null}
            </MeterMeta>

            <MultiplesGrid data-testid="multiples-grid">
              {(loading ? buildings.map(b => ({ ...b, points: [], total: 0 })) : tiles).map(
                tile => (
                  <MultiplesTile
                    key={tile.id}
                    type="button"
                    data-testid={`multiple-${tile.id}`}
                    onClick={() => onSelectBuilding(tile.id, tile.name)}
                    aria-label={`${tile.name}: ${(
                      ('total' in tile ? tile.total : 0) || 0
                    ).toLocaleString()} in this period. Choose to scope the page to it.`}
                  >
                    <MultiplesTitle>
                      <span>{tile.name}</span>
                      <strong>{(tile.total || 0).toLocaleString()}</strong>
                    </MultiplesTitle>

                    {'error' in tile && tile.error ? (
                      <SectionCaption data-testid={`multiple-error-${tile.id}`}>
                        {tile.error}
                      </SectionCaption>
                    ) : (
                      <ChartArea $aspect={2.4} $minHeight={92}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart accessibilityLayer
                            data={'points' in tile ? tile.points : []}
                            margin={margins.mobile}
                            /* The shared syncId is what makes this a small
                               multiple rather than six charts: hovering the 12th
                               on one tile marks the 12th on all of them. */
                            syncId="dashboard-multiples"
                          >
                            <XAxis dataKey="label" {...xAxisTime} hide />
                            <YAxis {...yAxisCount} domain={domain} hide />
                            <Tooltip
                              content={
                                <DashSimpleTooltip
                                  tone={dataViz.series[0]}
                                  valueKey="value"
                                  labelKey="label"
                                />
                              }
                              cursor={cursor.line}
                            />
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={dataViz.series[0]}
                              strokeWidth={2}
                              fill={fillUrl(GRADIENT.area)}
                              fillOpacity={1}
                              {...soloPoint(
                                'points' in tile ? tile.points.length : 0,
                                dataViz.series[0]
                              )}
                              isAnimationActive={false}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </ChartArea>
                    )}
                  </MultiplesTile>
                )
              )}
            </MultiplesGrid>

            {loading ? (
              <SectionCaption data-testid="multiples-loading">
                Asking for {buildings.length} buildings, two at a time, behind everything
                else on this screen.
              </SectionCaption>
            ) : null}
          </>
        ) : null}
      </PanelSection>
    </PanelShell>
  );
};
